// @flow
import * as React from 'react';
import {SortableItem} from 'react-easy-sort';
import {Typography} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import {DragIndicator} from '@material-ui/icons';

import {colors} from '../../../assets/styles/colors';
import {getImageUrl} from '../../../utils/image_provider';
import {commaFormat} from '../../../utils/number';
import type {Product, MinimalProduct, Currency} from '../../../entities';
import {classNames} from '../../../utils/react/className';
import {useStoreCurrency} from '../../currency/utils';
import {useIsFeatureInTier} from '../../../utils/component';

const useStyles = makeStyles(theme => ({
  itemWrapper: {
    cursor: 'grab',
    backgroundColor: colors.grey200,
    borderRadius: '1rem',
    padding: '12px',
    userSelect: 'none',
    WebkitUserSelect: 'none',
    MozUserSelect: 'none',
    msUserSelect: 'none',

    '&:active': {
      cursor: 'grabbing',
    },
  },
  itemDisabled: {
    cursor: 'default',
    opacity: 0.6,
    pointerEvents: 'none',
  },
  itemContent: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
  },
  leftSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
  },
  rightSection: {
    display: 'flex',
    alignItems: 'center',
  },
  itemImg: {
    width: '48px',
    height: 'auto',
    minHeight: '48px',
    borderRadius: '.75rem',
    userSelect: 'none',
    WebkitUserSelect: 'none',
    MozUserSelect: 'none',
    msUserSelect: 'none',
    pointerEvents: 'none',
    WebkitUserDrag: 'none',
    WebkitTouchCallout: 'none',
    [theme.breakpoints.down('sm')]: {
      marginLeft: '0px',
    },
  },
  itemName: {
    color: colors.grey700,
    fontWeight: 600,
    fontSize: '14px',
    userSelect: 'none',
    WebkitUserSelect: 'none',
    MozUserSelect: 'none',
    msUserSelect: 'none',
  },
  currentPrice: {
    fontWeight: 600,
    fontSize: '14px',
    userSelect: 'none',
    WebkitUserSelect: 'none',
    MozUserSelect: 'none',
    msUserSelect: 'none',
    whiteSpace: 'nowrap',
  },
  containerOffer: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row-reverse',
  },
  realPrice: {
    fontSize: '14px',
    fontWeight: 500,
    textDecoration: 'line-through',
    color: '#B8B8C7',
    marginRight: '1rem',
    userSelect: 'none',
    WebkitUserSelect: 'none',
    MozUserSelect: 'none',
    msUserSelect: 'none',
  },
}));

export type OrganizeProductItemProps = {
  product: MinimalProduct | Product,
  currency?: Currency,
  disabled?: boolean,
  hasTaxesFeature?: boolean,
};

function getProductTaxFactor(
  product: MinimalProduct | Product,
  hasTaxesFeature?: boolean
): number {
  if (hasTaxesFeature && product && product.taxes) {
    const taxes = product.taxes;
    return parseFloat(
      taxes.reduce((acc, curr) => acc + parseFloat(curr), 1).toFixed(2)
    );
  }
  return 1;
}

function OrganizeProductItem({product, disabled}: OrganizeProductItemProps) {
  const classes = useStyles();

  const hasTaxesFeature = useIsFeatureInTier('TAXES');
  const currency = useStoreCurrency();

  const taxFactor = React.useMemo(
    () => getProductTaxFactor(product, hasTaxesFeature),
    [product, hasTaxesFeature]
  );

  return (
    <SortableItem key={product.id}>
      <div
        className={classNames(classes.itemWrapper, {
          [classes.itemDisabled]: !!disabled,
        })}
      >
        <div className={classes.itemContent}>
          <div className={classes.leftSection}>
            <DragIndicator />
            <img
              src={getImageUrl(product && product.mainImageId)}
              className={classes.itemImg}
              alt="drag-product"
            />
            <Typography className={classes.itemName}>{product.name}</Typography>
          </div>

          <div className={classes.rightSection}>
            {product && product.offerPrice ? (
              <div className={classes.containerOffer}>
                <Typography className={classes.currentPrice}>
                  {commaFormat(
                    parseFloat(product.offerPrice) * taxFactor,
                    currency ? currency.symbol : ''
                  )}
                </Typography>
                <Typography className={classes.realPrice}>
                  {commaFormat(
                    parseFloat(product.price) * taxFactor,
                    currency ? currency.symbol : ''
                  )}
                </Typography>
              </div>
            ) : (
              <Typography className={classes.currentPrice}>
                {commaFormat(
                  parseFloat(product.price) * taxFactor,
                  currency ? currency.symbol : ''
                )}
              </Typography>
            )}
          </div>
        </div>
      </div>
    </SortableItem>
  );
}

export default React.memo<OrganizeProductItemProps>(OrganizeProductItem);
